import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';

const pulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50%       { opacity: 0.5; transform: scale(0.7); }
`;

const blink = keyframes`
  0%, 80%, 100% { opacity: 0.3; }
  40%           { opacity: 1; }
`;

const Card = styled.div`
  border: 0.5px solid #e8e8e8;
  border-radius: 10px;
  padding: 1rem 1.25rem;
  background: white;
  font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 0.875rem;
  gap: 8px;
`;

const TitleGroup = styled.div`
  flex: 1;
  min-width: 0;
`;

const Title = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const TitleIcon = styled.svg`
  flex-shrink: 0;
  color: #e97024;
`;

const Subtitle = styled.div`
  font-size: 11.5px;
  color: #999;
  margin-top: 3px;
`;

const RightGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 10.5px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 99px;
  background: ${({ $complete }) => ($complete ? '#EAF3DE' : '#FFF3E8')};
  color: ${({ $complete }) => ($complete ? '#3B6D11' : '#a84d0a')};
`;

const StatusDot = styled.span`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: ${({ $complete }) => ($complete ? '#3B6D11' : '#e97024')};
  animation: ${({ $complete }) => ($complete ? 'none' : pulse)} 1.2s ease-in-out infinite;
`;

const CloseBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: none;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  color: #999;
  padding: 0;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: #f0f0f0;
    color: #333;
  }

  svg { display: block; }
`;

const TrackBar = styled.div`
  height: 3px;
  background: #f0f0f0;
  border-radius: 99px;
  overflow: hidden;
  margin-bottom: 0.875rem;
`;

const TrackFill = styled.div`
  height: 100%;
  border-radius: 99px;
  background: #e97024;
  width: ${({ $pct }) => $pct}%;
  transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
`;

const StepsRow = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 0.75rem;
`;

const StepItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  position: relative;

  &:not(:last-child)::after {
    content: '';
    position: absolute;
    top: 10px;
    left: calc(50% + 11px);
    right: calc(-50% + 11px);
    height: 1px;
    background: ${({ $done }) => ($done ? '#e97024' : '#e8e8e8')};
    transition: background 0.4s;
  }
`;

const StepDot = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 1.5px solid ${({ $done, $active }) => ($done || $active ? '#e97024' : '#e0e0e0')};
  background: ${({ $done }) => ($done ? '#e97024' : 'white')};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  color: ${({ $done }) => ($done ? 'white' : '#999')};
  margin-bottom: 5px;
  position: relative;
  z-index: 1;
  flex-shrink: 0;
  transition: all 0.3s;
`;

const ActiveDotInner = styled.span`
  position: absolute;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #e97024;
  animation: ${pulse} 1.2s ease-in-out infinite;
`;

const CheckIcon = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
    <path d="M2 5l2.5 2.5L8 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const StepLabel = styled.div`
  font-size: 10px;
  color: ${({ $active, $done }) => ($active ? '#e97024' : $done ? '#666' : '#bbb')};
  text-align: center;
  line-height: 1.3;
  white-space: pre-line;
  font-weight: ${({ $active }) => ($active ? '500' : '400')};
  transition: color 0.3s;
`;

const StatusLine = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 18px;
`;

const BlinkDots = styled.span`
  display: flex;
  gap: 3px;
`;

const BlinkDot = styled.span`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: #bbb;
  animation: ${blink} 1.2s ease-in-out ${({ $delay }) => $delay || '0s'} infinite;
`;

const StatusText = styled.span`
  font-size: 12px;
  color: #888;
  font-style: italic;
`;

const STEPS = [
  {
    label: 'Classifying\nprompt',
    statusText: 'Classifying prompt…',
  },
  {
    label: 'Querying\nregister',
    statusText: 'Querying Requirements Register…',
  },
  {
    label: 'Pass 1:\nStatements',
    statusText: 'Pass 1 — extracting compliance statements…',
  },
  {
    label: 'Pass 2:\nCompare',
    statusText: 'Pass 2 — comparing against requirements…',
  },
  {
    label: 'Compiling\nfindings',
    statusText: 'Compiling findings…',
  },
];

const FILL_PCTS = [8, 25, 50, 75, 95];

export default function ComplianceScanProgress({
  complianceType = 'Building Code',
  complianceSubcategory = '',
  deliverableCount = 1,
  stepIndex = 0,
  isComplete = false,
  issuesFound,
  onDismiss,
}) {
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!isComplete) setDismissed(false);
  }, [isComplete, complianceType]);

  if (dismissed) return null;

  const activeStep = Math.min(Math.max(0, stepIndex), STEPS.length - 1);
  const pct = isComplete ? 100 : FILL_PCTS[activeStep];
  const currentStep = STEPS[activeStep];

  const subtitleParts = [complianceType, complianceSubcategory].filter(Boolean).join(' · ');
  const docLabel = `${deliverableCount} deliverable${deliverableCount !== 1 ? 's' : ''} selected`;
  const subtitle = isComplete
    ? issuesFound != null
      ? `Findings ready — ${issuesFound} issue${issuesFound !== 1 ? 's' : ''} in the Compliance Dashboard`
      : 'Findings ready in the Compliance Dashboard'
    : `${subtitleParts} — ${docLabel}`;

  const handleDismiss = () => {
    setDismissed(true);
    onDismiss?.();
  };

  return (
    <Card role="status" aria-label="Compliance scan progress">
      <Header>
        <TitleGroup>
          <Title>
            <TitleIcon
              width="14"
              height="14"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </TitleIcon>
            Running compliance check
          </Title>
          <Subtitle>{subtitle}</Subtitle>
        </TitleGroup>

        <RightGroup>
          <StatusBadge $complete={isComplete}>
            <StatusDot $complete={isComplete} />
            {isComplete ? 'Complete' : 'Scanning'}
          </StatusBadge>

          {isComplete && (
            <CloseBtn onClick={handleDismiss} aria-label="Dismiss scan progress">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                <path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </CloseBtn>
          )}
        </RightGroup>
      </Header>

      <TrackBar>
        <TrackFill $pct={pct} />
      </TrackBar>

      <StepsRow role="list">
        {STEPS.map((step, i) => {
          const done = i < activeStep || isComplete;
          const active = i === activeStep && !isComplete;
          return (
            <StepItem key={i} $done={done} role="listitem">
              <StepDot $done={done} $active={active}>
                {done ? <CheckIcon /> : active ? <ActiveDotInner /> : i + 1}
              </StepDot>
              <StepLabel $active={active} $done={done}>
                {step.label}
              </StepLabel>
            </StepItem>
          );
        })}
      </StepsRow>

      {!isComplete && (
        <StatusLine aria-live="polite">
          <BlinkDots aria-hidden="true">
            <BlinkDot $delay="0s" />
            <BlinkDot $delay="0.2s" />
            <BlinkDot $delay="0.4s" />
          </BlinkDots>
          <StatusText>{currentStep.statusText}</StatusText>
        </StatusLine>
      )}
    </Card>
  );
}
